import { useEffect, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { CENOX_HOME, businessMemberships, nextFrom, withNext } from './lib/intent';
import { BusinessStartPage } from './pages/business/BusinessStartPage';
import { AccountPage } from './pages/account/AccountPage';
import { RequireModule } from './components/RequireModule';
import { PlatformAdminPage } from './pages/platform/PlatformAdminPage';
import { AppShell } from './layouts/AppShell';
import { LocationProvider } from './lib/locationContext';
import { useWorkerHome } from './lib/workerHome';
import { Spinner } from './components/ui';

import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { SalesPage } from './pages/sales/SalesPage';
import { ShopEntryPage } from './pages/shop/ShopEntryPage';
import { CataloguePage } from './pages/catalogue/CataloguePage';
import { ExpensesPage } from './pages/expenses/ExpensesPage';
import { LoansPage } from './pages/loans/LoansPage';
import { LoanDetailPage } from './pages/loans/LoanDetailPage';
import { LoanReportsPage } from './pages/loans/LoanReportsPage';
import { LoanSettingsPage } from './pages/loans/LoanSettingsPage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage';
import { StaffPage } from './pages/staff/StaffPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { BillingPage } from './pages/billing/BillingPage';
import { TestCheckoutPage } from './pages/billing/TestCheckoutPage';
import { ImportPage } from './pages/imports/ImportPage';
import { AssistantPage } from './pages/assistant/AssistantPage';
import { AccountabilityPage } from './pages/accountability/AccountabilityPage';
import { RequireCounterCash } from './components/RequireCounterCash';
import { RiderEarningsPage } from './pages/finance/RiderEarningsPage';
import { DailyRiderEarningsPage } from './pages/finance/DailyRiderEarningsPage';
import { CompanyEarningsPage } from './pages/finance/CompanyEarningsPage';
import { InvestmentsPage } from './pages/investments/InvestmentsPage';
import { InvestorProfilePage } from './pages/investments/InvestorProfilePage';
import { MoneyAccountsPage } from './pages/money/MoneyAccountsPage';
import {
  StockCountsPage,
  StockCountSessionPage,
} from './pages/accountability/StockCountPage';
import { ReceivingPage, ReceiptSessionPage } from './pages/receiving/ReceivingPage';
import { DeliveriesPage } from './pages/deliveries/DeliveriesPage';
import { LogisticsLayout } from './pages/logistics/LogisticsLayout';
import { LogisticsOverviewPage } from './pages/logistics/OverviewPage';
import { LogisticsDeliveriesPage } from './pages/logistics/DeliveriesPage';
import { LogisticsRidersPage } from './pages/logistics/RidersPage';
import { LogisticsSettlementsPage } from './pages/logistics/SettlementsPage';
import { LogisticsReportsPage } from './pages/logistics/ReportsPage';
import { LogisticsReturnsPage } from './pages/logistics/ReturnsPage';
import { LogisticsImportPage } from './pages/logistics/ImportPage';
import { RiderFinanceLayout } from './pages/riderFinance/RiderFinanceLayout';
import { RiderRemittancePage } from './pages/riderFinance/RemittancePage';
import { HirePurchasePage } from './pages/riderFinance/HirePurchasePage';
import { BikeRegisterPage } from './pages/riderFinance/BikeRegisterPage';
import { InvestorDetailPage } from './pages/riderFinance/InvestorDetailPage';
import { RidersPage } from './pages/riderFinance/RidersPage';
import { NewDeliveryPage } from './pages/deliveries/NewDeliveryPage';
import { DeliveryDetailPage } from './pages/deliveries/DeliveryDetailPage';
import { ApprovalsPage as DeliveryApprovalsPage } from './pages/deliveries/ApprovalsPage';
import { PriceRulesPage as DeliveryPriceRulesPage } from './pages/deliveries/PriceRulesPage';
import { DeadlinesPage as DeliveryDeadlinesPage } from './pages/deliveries/DeadlinesPage';
import { WorkspacePage } from './pages/workspace/WorkspacePage';
import { RequireWorkerAuth } from './pages/workspace/RequireWorkerAuth';
import { HomeDashboard } from './pages/home/HomeDashboard';
import { CenoxShell } from './layouts/CenoxShell';
import { ProductPage } from './pages/products/ProductPage';
import { AiEntry, ChatEntry } from './pages/ai/AiEntry';
import { LocationsPage } from './pages/locations/LocationsPage';
import * as Pharmacy from './pages/pharmacy';
import { LocationLoginPage } from './pages/auth/LocationLoginPage';
import { LocationLinkEntry, NotForLocationSessions } from './lib/locationSession';

/**
 * Two gates, and they answer different questions.
 *
 *   RequireAuth       is there a CenoX identity?   no → /login?next=…
 *   RequireBusiness   is there a Business to open? no → /business (create / join)
 *
 * A CenoX account is global and product-neutral. Somebody who signed up for
 * CenoX AI or Automation has an identity and no business, and nothing outside
 * CenoX Business may send them to business onboarding. Only the Business
 * shell asks for a business — and when there is none it offers to create or
 * join one, at `/business`, rather than demanding it.
 */
/**
 * CenoX Business's front door, which is not the same door for everyone.
 *
 * A worker is sent to the location they work in; everyone else gets the
 * dashboard. Decided here rather than at login so it holds for every way in —
 * accepting an invitation, signing in again next morning, or typing the address
 * from memory.
 */
function Home(): ReactNode {
  const home = useWorkerHome();

  if (home.state === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner label="Opening your workspace" />
      </div>
    );
  }
  if (home.state === 'workspace') return <Navigate to={`/w/${home.branchId}`} replace />;
  if (home.state === 'choose') return <ChooseLocation branches={home.branches} />;
  if (home.state === 'unassigned') return <UnassignedWorker />;
  return <DashboardPage />;
}

/** A worker who covers more than one place says which one they are at today. */
function ChooseLocation({
  branches,
}: {
  branches: Array<{ id: string; name: string }>;
}): ReactNode {
  return (
    <div className="mx-auto max-w-md p-4">
      <h1 className="text-lg font-semibold text-slate-900">Where are you working today?</h1>
      <ul className="mt-4 space-y-2">
        {branches.map((b) => (
          <li key={b.id}>
            <Link
              to={`/w/${b.id}`}
              className="block rounded-xl border border-slate-200 bg-white px-4 py-3 font-medium text-slate-900 hover:border-indigo-300"
            >
              {b.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A worker nobody has assigned yet.
 *
 * Said plainly, because the alternative is an empty screen that looks broken
 * and a new hire wondering whether they did something wrong.
 */
function UnassignedWorker(): ReactNode {
  return (
    <div className="mx-auto max-w-md p-4 text-center">
      <h1 className="text-lg font-semibold text-slate-900">You are not at a location yet</h1>
      <p className="mt-2 text-sm text-slate-600">
        Ask the owner to assign you to the place you work. Until then there is nothing here for
        you to do — this is not an error.
      </p>
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Restoring your session" />
      </div>
    );
  }

  if (!user) {
    /*
      Remember where they were headed, in the URL, so signing in — or signing
      up from the link on the sign-in screen — returns them there. Somebody
      who opened /automation and created an account belongs in Automation.
    */
    const here = `${location.pathname}${location.search}`;
    const next = here === '/' ? null : here;
    return <Navigate to={withNext('/login', next)} replace />;
  }

  return <>{children}</>;
}

/**
 * The Business shell's own gate.
 *
 * No Business workspace: `/business` itself shows Create / Join, and any
 * deeper Business path goes there rather than to a shell with nothing in it.
 * A selection pointing at a non-Business workspace is moved to a Business one
 * before the shell renders, so the shell never opens on the wrong kind.
 */
function RequireBusiness({ children }: { children: ReactNode }): ReactNode {
  const { business, memberships, selectBusiness } = useAuth();
  const { pathname } = useLocation();
  const owned = businessMemberships(memberships);
  const usable = business !== null && owned.some((m) => m.businessId === business.businessId);
  const fallback = owned[0]?.businessId;

  useEffect(() => {
    if (!usable && fallback) selectBusiness(fallback);
  }, [usable, fallback, selectBusiness]);

  if (owned.length === 0) {
    return pathname === '/business' ? <BusinessStartPage /> : <Navigate to="/business" replace />;
  }
  if (!usable) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Opening your business" />
      </div>
    );
  }
  return <>{children}</>;
}

/** Business onboarding: signed in, and asked for only from inside CenoX Business. */
function BusinessOnboarding(): ReactNode {
  return (
    <RequireAuth>
      <OnboardingPage />
    </RequireAuth>
  );
}

function RedirectIfSignedIn({ children }: { children: ReactNode }): ReactNode {
  const { user, loading } = useAuth();
  const { search } = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner />
      </div>
    );
  }
  if (user) return <Navigate to={nextFrom(search) ?? CENOX_HOME} replace />;
  return <>{children}</>;
}

/** The old Remittance & Hire Purchase investor page: the same investor, in Finance. */
function MovedInvestor(): ReactNode {
  const { investorId } = useParams<{ investorId: string }>();
  return <Navigate to={`/finance/investors/${investorId ?? ''}`} replace />;
}

export function App(): ReactNode {
  /**
   * Defined once and mounted twice — `/` and `/home` are the same screen, not
   * two screens that happen to agree.
   */
  const platformHome = (
    <RequireAuth>
      <NotForLocationSessions>
        <CenoxShell rail={false}>
          <HomeDashboard />
        </CenoxShell>
      </NotForLocationSessions>
    </RequireAuth>
  );

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <LoginPage />
          </RedirectIfSignedIn>
        }
      />
      {/*
        Where a location signs in, from the link written on the wall behind its
        counter. Public like /login: the code in the path names the business,
        not the account, and the credential is still a username and a password.
      */}
      <Route
        path="/l/:code"
        element={
          <LocationLinkEntry>
            <LocationLoginPage />
          </LocationLinkEntry>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfSignedIn>
            <RegisterPage />
          </RedirectIfSignedIn>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/*
        Public routes reached from an email.

        Every path the API can put in an email has a route here, and the two
        invitation paths are ALIASES rather than a rename. Emails already in
        somebody's inbox cannot be edited, so retiring `/accept-invite` would
        quietly break every invitation sent before today — and the person
        holding one has no way to tell that the link, not their account, is the
        problem. See apps/api/src/modules/email/messages.ts for the senders.
      */}
      <Route path="/join/:token" element={<AcceptInvitePage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      {/*
        Business onboarding belongs to CenoX Business, not to signing up.

        `/onboarding` stays as an alias — it is on the list of root paths that
        are never moved — but it now leads into the Business product's own
        address rather than being the place every new account is sent.
      */}
      <Route path="/business/onboarding" element={<BusinessOnboarding />} />
      <Route path="/onboarding" element={<Navigate to="/business/onboarding" replace />} />

      {/*
        A worker's door into one location.
        
        Outside the shell on purpose: the shell is the owner's view of a whole
        business, and a worker opening the Holy Ghost link wants Holy Ghost and
        the two things they are allowed to do in it. Behind RequireAuth like
        everything else — the link carries no secret, and who signs in is what
        decides the answer.
      */}
      {/*
        CenoX Home — the platform, above any one product.

        Outside the Business shell deliberately, and outside LocationProvider:
        that shell's navigation is CenoX Business, and a location switcher is a
        question only one product asks.

        Mounted at BOTH paths from one element rather than two. `/` is now the
        canonical front door; `/home` keeps resolving because it was the
        canonical one for a while and links to it exist — in a browser history,
        a bookmark, a message somebody sent. A second copy of the element would
        be a second place to change.
      */}
      {/* The CenoX account itself — above every product and every workspace. */}
      <Route
        path="/account"
        element={
          <RequireAuth>
            <NotForLocationSessions>
              <AccountPage />
            </NotForLocationSessions>
          </RequireAuth>
        }
      />

      {/* CenoX staff. The API refuses anyone without the stored platform_admin role. */}
      <Route
        path="/platform-admin"
        element={
          <RequireAuth>
            <NotForLocationSessions>
              <PlatformAdminPage />
            </NotForLocationSessions>
          </RequireAuth>
        }
      />

      <Route path="/" element={platformHome} />
      <Route path="/home" element={platformHome} />

      {/*
        Where the product cards land.

        Enumerated rather than a `/:slug` wildcard, which would swallow every
        unknown path in the application — including the ones the Business shell
        owns. `business` is absent because it is no longer a placeholder: it
        has a real product behind it, mounted below.
      */}
      {/*
        CenoX AI, which is a product with a workspace rather than a placeholder.

        `/ai` itself and every section under it resolve here; the entry decides
        between the workspace and the locked landing page from the server's
        answer, so an unentitled workspace never renders the workspace chrome.
      */}
      {/*
        Chat — CenoX AI's conversations, at an address of their own.

        One route with an optional segment, not two: `/chat` → `/chat/<id>`
        happens mid-question when the first message starts a thread, and two
        routes would remount the screen and cut off the answer streaming in.
        Bare `/ai` (Chat's old address) forwards here; see AiEntry.
      */}
      <Route path="/chat/:conversationId?" element={<RequireAuth><NotForLocationSessions><ChatEntry /></NotForLocationSessions></RequireAuth>} />
      <Route path="/ai" element={<RequireAuth><NotForLocationSessions><AiEntry /></NotForLocationSessions></RequireAuth>} />
      <Route path="/ai/:section" element={<RequireAuth><NotForLocationSessions><AiEntry /></NotForLocationSessions></RequireAuth>} />

      {['verify', 'growth', 'automation', 'train', 'robotics', 'autonomous', 'intelligence'].map(
        (slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={
              <RequireAuth>
                <NotForLocationSessions>
                  <ProductPage />
                </NotForLocationSessions>
              </RequireAuth>
            }
          />
        ),
      )}

      {/*
        A counter's own link, which must work in both states: signed in it
        opens the workspace, signed out it routes to the sign-in THIS location
        uses rather than to the personal form it cannot complete.
      */}
      <Route
        path="/w/:branchId"
        element={
          <RequireWorkerAuth>
            <WorkspacePage />
          </RequireWorkerAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <RequireBusiness>
              {/*
                Above the shell, so the switcher and every screen under it read
                one selection. A per-screen location would be a screen that
                disagrees with the one beside it.
              */}
              <LocationProvider>
                <AppShell />
              </LocationProvider>
            </RequireBusiness>
          </RequireAuth>
        }
      >
        {/*
          CenoX Business, at its own address.

          It used to be the index of this shell, which is to say it owned `/`.
          It no longer does: the root is the platform, and this product is one
          of eight. Its inner screens keep the paths they have always had —
          moving those too would be a second change wearing the same coat, and
          every link, bookmark and redirect that points at one of them would
          break for a tidier URL.
        */}
        <Route path="/business" element={<Home />} />
        {/*
          Pharmacy. Every screen behind the module; each one also checks the
          permission it needs, and the API decides every request regardless.
        */}
        <Route path="/pharmacy" element={<RequireModule module="pharmacy"><Pharmacy.PharmacyDashboardPage /></RequireModule>} />
        <Route path="/pharmacy/pos" element={<RequireModule module="pharmacy"><Pharmacy.PosPage /></RequireModule>} />
        <Route path="/pharmacy/pos/outage" element={<RequireModule module="pharmacy"><Pharmacy.OutageEntryPage /></RequireModule>} />
        <Route path="/pharmacy/stock" element={<RequireModule module="pharmacy"><Pharmacy.MedicinesPage /></RequireModule>} />
        <Route path="/pharmacy/stock/medicines/:id" element={<RequireModule module="pharmacy"><Pharmacy.MedicineDetailPage /></RequireModule>} />
        <Route path="/pharmacy/stock/products/:id" element={<RequireModule module="pharmacy"><Pharmacy.ProductReviewPage /></RequireModule>} />
        <Route path="/pharmacy/stock/entry" element={<RequireModule module="pharmacy"><Pharmacy.StockEntryPage /></RequireModule>} />
        <Route path="/pharmacy/stock/batches" element={<RequireModule module="pharmacy"><Pharmacy.BatchesPage /></RequireModule>} />
        <Route path="/pharmacy/stock/adjustments" element={<RequireModule module="pharmacy"><Pharmacy.AdjustmentsPage /></RequireModule>} />
        <Route path="/pharmacy/stock/counts" element={<RequireModule module="pharmacy"><Pharmacy.CountsPage /></RequireModule>} />
        <Route path="/pharmacy/stock/counts/:id" element={<RequireModule module="pharmacy"><Pharmacy.CountDetailPage /></RequireModule>} />
        <Route path="/pharmacy/stock/recalls" element={<RequireModule module="pharmacy"><Pharmacy.RecallsPage /></RequireModule>} />
        <Route path="/pharmacy/stock/recalls/:id" element={<RequireModule module="pharmacy"><Pharmacy.RecallDetailPage /></RequireModule>} />
        <Route path="/pharmacy/stock/storage" element={<RequireModule module="pharmacy"><Pharmacy.StoragePage /></RequireModule>} />
        <Route path="/pharmacy/prescriptions" element={<RequireModule module="pharmacy"><Pharmacy.PrescriptionsPage /></RequireModule>} />
        <Route path="/pharmacy/prescriptions/:id" element={<RequireModule module="pharmacy"><Pharmacy.PrescriptionDetailPage /></RequireModule>} />
        <Route path="/pharmacy/patients" element={<RequireModule module="pharmacy"><Pharmacy.PatientsPage /></RequireModule>} />
        <Route path="/pharmacy/patients/:id" element={<RequireModule module="pharmacy"><Pharmacy.PatientDetailPage /></RequireModule>} />
        <Route path="/pharmacy/purchasing" element={<RequireModule module="pharmacy"><Pharmacy.PurchasingPage /></RequireModule>} />
        <Route path="/pharmacy/transfers" element={<RequireModule module="pharmacy"><Pharmacy.TransfersPage /></RequireModule>} />
        <Route path="/pharmacy/finance" element={<RequireModule module="pharmacy"><Pharmacy.PharmacyFinancePage /></RequireModule>} />
        <Route path="/pharmacy/reports" element={<RequireModule module="pharmacy"><Pharmacy.PharmacyReportsPage /></RequireModule>} />
        <Route path="/pharmacy/settings" element={<RequireModule module="pharmacy"><Pharmacy.PharmacySettingsPage /></RequireModule>} />
        <Route path="/pharmacy/settings/import" element={<RequireModule module="pharmacy"><Pharmacy.PharmacyImportPage /></RequireModule>} />
        <Route path="/shop" element={<RequireModule module="inventory"><ShopEntryPage /></RequireModule>} />
        <Route path="/catalogue" element={<RequireModule module="inventory"><CataloguePage /></RequireModule>} />
        {/*
          Gated on `sales_pos`, which is a module like any other now.

          It used to be structural — "every other module assumes sales exist" —
          and that is not true of a courier, which records deliveries and
          weekly remittance and never rings up a sale. Nothing in the API gates
          on `sales_pos`, so switching it off hides this screen and keeps every
          sale already recorded; RequireModule says exactly that and links to
          the switch.
        */}
        <Route path="/sales" element={<RequireModule module="sales_pos"><SalesPage /></RequireModule>} />
        <Route path="/expenses" element={<ExpensesPage />} />
        {/* Loans sit with Expenses in the menu, and in their own ledger everywhere else. */}
        <Route path="/loans" element={<LoansPage />} />
        <Route path="/loans/reports" element={<LoanReportsPage />} />
        <Route path="/loans/settings" element={<LoanSettingsPage />} />
        <Route path="/loans/:id" element={<LoanDetailPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/staff" element={<StaffPage />} />
        {/* Reports are one component behind six period routes, so each
            period is linkable, bookmarkable and back-button friendly. */}
        <Route path="/reports" element={<Navigate to="/reports/daily" replace />} />
        <Route path="/reports/:period" element={<ReportsPage />} />
        <Route path="/imports" element={<ImportPage />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="/accountability" element={<RequireCounterCash><AccountabilityPage /></RequireCounterCash>} />
        <Route path="/finance/daily-rider-earnings" element={<RequireModule module="delivery"><DailyRiderEarningsPage /></RequireModule>} />
        {/* Rider settlement was removed. Old links land on Finance rather than a dead page. */}
        <Route path="/finance/rider-settlement/*" element={<Navigate to="/finance/daily-rider-earnings" replace />} />
        <Route path="/finance/rider-earnings" element={<RiderEarningsPage />} />
        <Route path="/finance/company-earnings" element={<CompanyEarningsPage />} />
        <Route path="/finance/accounts" element={<MoneyAccountsPage />} />
        <Route path="/finance/investors" element={<InvestmentsPage />} />
        <Route path="/finance/investors/:investorId" element={<InvestorProfilePage />} />
        {/* One investor's vehicle weeks: each bike-week's remittance and how it divides. */}
        <Route
          path="/finance/investors/:investorId/weeks"
          element={<RequireModule module="rider_finance"><InvestorDetailPage /></RequireModule>}
        />
        <Route path="/stock-counts" element={<RequireModule module="inventory"><StockCountsPage /></RequireModule>} />
        <Route path="/stock-counts/:id" element={<RequireModule module="inventory"><StockCountSessionPage /></RequireModule>} />
        <Route path="/locations" element={<LocationsPage />} />
        {/*
          Logistics, at /business/logistics.

          A prefixed group rather than more siblings of `/deliveries`: this is
          one product area with seven screens that share a tab strip, and the
          existing delivery routes keep their paths so no link or bookmark
          pointing at one of them breaks.
        */}
        <Route path="/business/logistics" element={<RequireModule module="delivery"><LogisticsLayout /></RequireModule>}>
          <Route index element={<LogisticsOverviewPage />} />
          <Route path="deliveries" element={<LogisticsDeliveriesPage />} />
          <Route path="riders" element={<LogisticsRidersPage />} />
          <Route path="settlements" element={<LogisticsSettlementsPage />} />
          <Route path="reports" element={<LogisticsReportsPage />} />
          <Route path="returns" element={<LogisticsReturnsPage />} />
          <Route path="import" element={<LogisticsImportPage />} />
        </Route>
        {/*
          Remittance & Hire Purchase, beside Logistics rather than inside it.

          Gated on `rider_finance`, its own capability: a courier that moves
          parcels has Logistics without a fleet its riders are buying, and a
          shop that sells bikes to its drivers on instalments has this without
          Logistics. Inferring one from the other would have described Ceno Hub
          and nobody else.
        */}
        <Route
          path="/business/rider-finance"
          element={<RequireModule module="rider_finance"><RiderFinanceLayout /></RequireModule>}
        >
          <Route index element={<RiderRemittancePage />} />
          <Route path="hire-purchase" element={<HirePurchasePage />} />
          <Route path="bikes" element={<BikeRegisterPage />} />
          {/* Investors moved to Finance → Investors; old links and bookmarks follow them. */}
          <Route path="investors" element={<Navigate to="/finance/investors" replace />} />
          <Route path="investors/:investorId" element={<MovedInvestor />} />
          <Route path="riders" element={<RidersPage />} />
        </Route>
        <Route path="/deliveries" element={<RequireModule module="delivery"><DeliveriesPage /></RequireModule>} />
        <Route path="/deliveries/new" element={<RequireModule module="delivery"><NewDeliveryPage /></RequireModule>} />
        <Route path="/deliveries/:id" element={<RequireModule module="delivery"><DeliveryDetailPage /></RequireModule>} />
        {/* Deciding exceptions and setting prices: the owner's side of the delivery book. */}
        <Route path="/business/deliveries/approvals" element={<RequireModule module="delivery"><DeliveryApprovalsPage /></RequireModule>} />
        <Route path="/business/deliveries/prices" element={<RequireModule module="delivery"><DeliveryPriceRulesPage /></RequireModule>} />
        <Route path="/business/deliveries/deadlines" element={<RequireModule module="delivery"><DeliveryDeadlinesPage /></RequireModule>} />
        <Route path="/receiving" element={<RequireModule module="inventory"><ReceivingPage /></RequireModule>} />
        <Route path="/receiving/:id" element={<RequireModule module="inventory"><ReceiptSessionPage /></RequireModule>} />
        <Route path="/billing" element={<BillingPage />} />
        {/* Test-mode stand-in for the provider's hosted page. */}
        <Route path="/billing/test-checkout" element={<TestCheckoutPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
